import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { restaurantExtrasApi } from "@/api/restaurantExtras";
import type { RestaurantExtra } from "@/types";

const CURRENCIES = [
  { value: "BRL", label: "BRL - Brazilian Real" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
];

const extraSchema = z.object({
  name: z
    .string()
    .min(1, "Extra name is required")
    .max(100, "Name is too long"),
  price: z.coerce
    .number()
    .min(0, "Price must be 0 or greater")
    .max(999999, "Price is too high"),
  currency: z.string().min(1, "Currency is required"),
});

type ExtraFormData = z.infer<typeof extraSchema>;

interface ExtraDialogProps {
  open: boolean;
  onClose: () => void;
  extra: RestaurantExtra | null;
}

export function ExtraDialog({ open, onClose, extra }: ExtraDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!extra;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(extraSchema),
    defaultValues: {
      name: extra?.name || "",
      price: extra?.price || 0,
      currency: extra?.currency || "BRL",
    },
  });

  const currency = watch("currency");

  // Reset form when dialog opens/closes or extra changes
  useEffect(() => {
    if (open) {
      reset({
        name: extra?.name || "",
        price: extra?.price || 0,
        currency: extra?.currency || "BRL",
      });
    }
  }, [open, extra, reset]);

  const createMutation = useMutation({
    mutationFn: restaurantExtrasApi.createExtra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurantExtras"] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { name?: string; price?: number; currency?: string };
    }) => restaurantExtrasApi.updateExtra(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurantExtras"] });
      onClose();
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: ExtraFormData) => {
    if (isEditing && extra) {
      updateMutation.mutate({ id: extra.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Extra" : "Create Extra"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the extra details"
              : "Add a new reusable extra for your products"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Extra Cheese"
                {...register("name")}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("price")}
                  aria-invalid={!!errors.price}
                />
                {errors.price && (
                  <p className="text-sm text-destructive">
                    {errors.price.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={currency}
                  onValueChange={(value) => setValue("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.currency && (
                  <p className="text-sm text-destructive">
                    {errors.currency.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
